import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Send } from "lucide-react";
import { useAuth } from "@/contexts/useAuth";

const Footer = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canAccessPortal = !!user && (user.isAdmin || user.isProvisionedClient);
  const sectionLinks = [
    { to: "/#services", label: "Services" },
    { to: "/#process", label: "Process" },
    { to: "/#pricing", label: "Pricing" },
    { to: "/#faq", label: "FAQ" },
  ];

  const handleStartProject = () => {
    if (canAccessPortal) {
      navigate("/portal");
    } else {
      navigate("/contact");
    }
  };

  return (
    <div className="px-4 pb-4">
      {/* CTA Banner */}
      <div className="py-14 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <div className="inline-flex flex-col sm:flex-row items-center gap-4 rounded-full pl-8 pr-3 py-3 bg-foreground text-background">
            <span className="text-base font-medium">Start your project with us today!</span>
            <Button
              variant="secondary"
              size="lg"
              className="gap-2 group bg-background text-foreground hover:bg-background/90 rounded-full"
              onClick={handleStartProject}
            >
              <Send className="w-4 h-4" />
              <span>Start a Project</span>
            </Button>
          </div>
        </div>
      </div>

      <footer className="bg-foreground text-background rounded-3xl">
        {/* Main footer */}
        <div className="py-16 px-8 md:px-12">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
              {/* Left side */}
              <div>
                <Link to="/" className="flex items-center gap-2 mb-4 group">
                  <span className="font-medium text-lg tracking-tight">5AM Founders</span>
                </Link>
                <p className="text-background/60 max-w-sm leading-relaxed">
                  The technical partner for founders building the future. We design, build, and scale your ideas.
                </p>
              </div>

              {/* Right side - Links */}
              <div className="flex justify-start md:justify-end">
                <ul className="space-y-3">
                  {sectionLinks.map((link) => (
                    <li key={link.to}>
                      <Link to={link.to} className="text-background/70 hover:text-background transition-colors duration-300">
                        {link.label}
                      </Link>
                    </li>
                  ))}
                  <li>
                    <a href="mailto:team@5amfounders.com" className="text-background/70 hover:text-background transition-colors duration-300">
                      Contact
                    </a>
                  </li>
                  <li>
                    <Link to="/terms" className="text-background/70 hover:text-background transition-colors duration-300">
                      Terms &amp; Conditions
                    </Link>
                  </li>
                  <li>
                    <Link to="/privacy" className="text-background/70 hover:text-background transition-colors duration-300">
                      Privacy Policy
                    </Link>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-background/10 py-6 px-8 md:px-12">
          <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-sm text-background/40">
              5AM Founders © 2025 All rights reserved
            </p>
            <div className="flex items-center gap-5">
              <Link to="/terms" className="text-sm text-background/40 hover:text-background transition-colors duration-300">
                Terms
              </Link>
              <Link to="/privacy" className="text-sm text-background/40 hover:text-background transition-colors duration-300">
                Privacy
              </Link>
              <a href="https://www.instagram.com/brandonlei.movie/" target="_blank" rel="noopener noreferrer" className="text-background/40 hover:text-background transition-all duration-300 hover:scale-110">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path fillRule="evenodd" d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z" clipRule="evenodd" />
                </svg>
              </a>
              <a href="https://www.youtube.com/@brandonlei.videos" target="_blank" rel="noopener noreferrer" className="text-background/40 hover:text-background transition-all duration-300 hover:scale-110">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path fillRule="evenodd" d="M19.812 5.418c.861.23 1.538.907 1.768 1.768C21.998 8.746 22 12 22 12s0 3.255-.418 4.814a2.504 2.504 0 0 1-1.768 1.768c-1.56.419-7.814.419-7.814.419s-6.255 0-7.814-.419a2.505 2.505 0 0 1-1.768-1.768C2 15.255 2 12 2 12s0-3.255.417-4.814a2.507 2.507 0 0 1 1.768-1.768C5.744 5 11.998 5 11.998 5s6.255 0 7.814.418ZM15.194 12 10 15V9l5.194 3Z" clipRule="evenodd" />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Footer;
